<template lang="pug">
    v-container.timerPage
        v-layout(row wrap justify-space-between)
            v-flex(xs12)
                layout.contentTransparent(fluid)

                    // Title
                    title-component(
                        :title="$t('timer.title')"
                        type="h2"
                    )

                    template
                        div.contentWrapper
                            v-layout(row wrap justify-space-between)
                                v-flex(xs12 md6)
                                    div.overlay

                                        // Duration
                                        div.sliderContainer
                                            div.sliderInfo
                                                div.timerBlockLabel {{ `${$t('timer.durationLabel')} - ${timerValue}` }}

                                            v-slider(
                                                v-model="model.timer"
                                                color="action"
                                                thumb-color="action"
                                                min="1"
                                                max="120"
                                                persistent-hint
                                                hide-details
                                                track-color="white"
                                            )

                                            div.sliderLabels
                                                div.sliderLabel 1min

                                                div.sliderLabel max 2h

                                v-flex(xs12 md6)
                                    div.overlay.overlayMargin(v-if="model.timer > 1")

                                        // Interval
                                        transition(name="fade")
                                            div.sliderContainer
                                                div.sliderInfo
                                                    div.timerBlockLabel {{ `${$t('timer.intervalLabel')} - ${intervalValue}` }}

                                                v-slider(
                                                    v-model="model.interval"
                                                    color="action"
                                                    thumb-color="action"
                                                    min="0"
                                                    :max="maxInterval"
                                                    persistent-hint
                                                    hide-details
                                                    track-color="white"
                                                )

                                                div.sliderLabels
                                                    div.sliderLabel none

                                                    div.sliderLabel max {{ maxInterval }} mins

                                // Sounds
                                v-flex.mt-3(xs12)
                                    div.overlay

                                        div.timerBlockLabel {{ $t('timer.soundsLabel')}}

                                        v-layout.sounds.pb-0(row wrap)
                                            v-flex(xs4 sm4 md3 lg2 v-for="(item, key) in $t('timer.sounds')" :key="key")
                                                div.soundButton(
                                                     @click="playSound(item)"
                                                    :key="key"
                                                    :style="{ backgroundImage: `url(${item.image})`}"
                                                )
                                                    div.soundButtonLabel
                                                        div(
                                                            :class="{active: model.soundUrl === item.sound}"
                                                        ) {{ (model.soundUrl === item.sound && item.name !== 'No Sound') ? 'Selected' : item.name }}

                                v-flex.mt-4(xs12)
                                    div.text-sm-right.text-xs-center

                                        // Start button
                                        button-component.ml-0.mr-0.startButton(
                                            :action="startTimer"
                                            color="action"
                                            large
                                        ) {{ $t('defaultButtons.start') }}

        // Timer modal window
        timer-modal(v-if="showTimerModal" :timer-settings="model" :dialogOpen="showTimerModal" :timerCallback="timerCallback")


        v-dialog.fixedDialog(
            v-model="showMood"
            persistent
            no-click-animation
        )
            // Mood capture
            mood-component(v-if="showMood" @close="closeMood" :launchCallback="launchTimer")
</template>

<script>
    import Layout from '../Layout/Layout';
    import TimerModal from './TimerModal';
    import Title from '~components/Title';
    import Button from '~components/Button';
    import NewMood from '~components/NewMood';
    import { mapActions, mapGetters } from 'vuex';

    export default {
        components: {
            Layout,
            TimerModal,
            TitleComponent: Title,
            ButtonComponent: Button,
            MoodComponent: NewMood
        },
        data() {
            return {
                sound: null,
                showTimerModal: false,
                model: {
                    timer: 1,
                    interval: 0,
                    soundUrl: '',
                    imageUrl: ''
                }
            };
        },
        computed: {
            ...mapGetters('sessions', ['showMood', 'session']),

            timerValue() {
                if (this.model.timer < 60) return `${this.model.timer} mins`;
                return `${parseInt(this.model.timer / 60)} hours ${
                    this.model.timer % 60 ? `${this.model.timer % 60} mins` : ''
                }`;
            },

            maxInterval() {
                return parseInt(this.model.timer / 2);
            },

            intervalValue() {
                return this.model.interval
                    ? `Every ${this.model.interval} mins`
                    : 'None';
            }
        },
        async created() {
            this.restoreTimerSettings();
            await this.read({ id: process.env.VUE_APP_TIMER_SESSION_ID });
        },
        beforeDestroy() {
            this.noSound();
        },
        methods: {
            ...mapActions('sessions', ['read', 'setShowMood']),

            noSound() {
                this.model.soundUrl = null;
                if (this.sound) {
                    this.sound.pause();
                    this.sound = null;
                }
                return this.model.soundUrl;
            },

            async playSound(item) {
                await this.noSound();

                this.model.soundUrl = item.sound;
                this.model.imageUrl = item.image;
                this.sound = new Audio(item.sound);

                this.sound.play();
            },

            startTimer() {
                if (this.sound) {
                    this.sound.pause();
                    this.sound = null;
                }
                this.saveTimerSettings();

                if (
                    this.session?.todayVote?.rateBefore ||
                    this.session?.todayVote?.isSkip ||
                    this.session?.todayVote?.isCompleted
                )
                    return this.launchTimer();

                this.setShowMood();
            },

            launchTimer() {
                this.showTimerModal = true;
            },

            closeMood() {
                this.setShowMood();
            },

            saveTimerSettings() {
                window.localStorage.setItem(
                    'timer',
                    JSON.stringify(this.model)
                );
            },

            restoreTimerSettings() {
                const savedTimer = JSON.parse(
                    window.localStorage.getItem('timer')
                );

                if (savedTimer) this.model = savedTimer;
            },

            timerCallback() {
                this.showTimerModal = !this.showTimerModal;
            }
        }
    };
</script>

<style lang="stylus" scoped>
    @import "../../../assets/style/_variables.styl"

    .contentWrapper
        width: 100%
        border-radius: 10px
        padding: 16px 0
        margin-bottom: 20px

    .overlay
        background: linear-gradient(90deg, rgba(255,255,255, 0.2) 0%, transparent 100%);
        padding: 16px
        border-radius: 10px

    .sliderInfo
        display: flex
        justify-content: space-between

    .sliderLabels
        display: flex
        justify-content: space-between

    .sliderLabel
        white-space: nowrap

    .timerBlockLabel
        white-space: nowrap
        text-transform: uppercase
        font-size: 14px

    >>> .v-slider__thumb
        width: 28px
        height: 28px
        left: -8px
        background: $theme.action !important
        border: 4px solid $theme.white !important

    .sounds
        padding: 16px 0 0

    .soundButton
        position: relative
        background-size: cover
        background-position: center
        width: 100%
        height: 120px
        margin-bottom: 16px
        border-radius: 10px
        overflow: hidden
        cursor: pointer
        transition: border-color 0.3s

        &:hover
            .soundButtonLabel div
                border-color: $theme.action

        img
            width: 100%

    .soundButtonLabel
        position: absolute
        top: 0
        left: 0
        width: 100%
        height: 100%
        display: flex
        align-items: center
        justify-content: center
        background: linear-gradient(180deg, rgba(2, 35, 65, 0.7) 0%, rgba(2, 35, 65, 0) 100%)

        div
            display: inline-block
            padding: 3px 10px
            border: 1px solid $theme.white
            border-radius: 10px
            font-size: 12px
            transition: 0.3s
            background: linear-gradient(180deg, rgba(2, 35, 65, 0.6) 0%, rgba(2, 35, 65, 0.2) 100%)

            &.active
                background: $theme.action

    .backgroundVideo
        z-index: 1
        position: fixed
        top: 0
        left: 0
        width: 100vw
        height: 100vh
        object-fit: cover
        filter: blur(6px)

    @media screen and (max-width: $grid-breakpoints.md - 1)
        .overlayMargin
            margin-top: 20px

    @media screen and (max-width: $grid-breakpoints.sm - 1)
        .timerPage
            margin-top: 12px
            margin-bottom: 90px

        .overlay
            padding: 10px

        .sounds
            padding: 12px 0

        .soundButton
            height: 70px

        .startButton
            width: 100%
</style>
